import * as React from 'react';

import {defineMessages, useIntl} from 'react-intl';
import {
  arrowRight,
  block,
  cancel,
  check,
  clock,
  dispute,
  disputeProtection,
  earlyFraudWarning,
  fee,
  info,
  refund,
  review,
  rule,
  shieldCheck,
  trial,
} from '@sail/icons/react/Icon';
import type {IconAsset} from '@sail/icons/types';
import {Badge, Icon, view} from '@sail/ui';
import type {BadgeProps} from '@sail/ui';

import {DISPLAY_STATUSES} from './statuses';
import type {DisplayStatus as Status} from './statuses';

const statusMessages = defineMessages({
  succeeded: {
    id: 'conversations_ui.payment.status.succeeded',
    description: 'The short status indicator for a payment that has succeeded.',
    defaultMessage: 'Succeeded',
  },
  pending: {
    id: 'conversations_ui.payment.status.pending',
    description:
      'The short status indicator for when a payment is still pending.',
    defaultMessage: 'Pending',
  },
  failed: {
    id: 'conversations_ui.payment.status.failed',
    description: 'The short status indicator for when a payment failed.',
    defaultMessage: 'Failed',
  },
  refunded: {
    id: 'conversations_ui.payment.status.refunded',
    description:
      'The short status indicator for when a payment has been refunded.',
    defaultMessage: 'Refunded',
  },
  reversed: {
    id: 'conversations_ui.payment.status.reversed',
    description:
      'The short status indicator for when a payment has been reversed.',
    defaultMessage: 'Reversed',
  },
  partialRefund: {
    id: 'conversations_ui.payment.status.partialRefund',
    description:
      'The short status indicator for a payment that has been partially refunded.',
    defaultMessage: 'Partial refund',
  },
  partiallyReversed: {
    id: 'conversations_ui.payment.status.partiallyReversed',
    description:
      'The short status indicator for a payment that has been partially reversed.',
    defaultMessage: 'Partially reversed',
  },
  canceled: {
    id: 'conversations_ui.payment.status.canceled',
    description:
      'The short status indicator for a payment that has been released or canceled.',
    defaultMessage: 'Canceled',
  },
  blocked: {
    id: 'conversations_ui.payment.status.blocked',
    description:
      'The short status indicator for when a payment was blocked (usually by the Radar product).',
    defaultMessage: 'Blocked',
  },
  expired: {
    id: 'conversations_ui.payment.status.expired',
    description:
      'The short status indicator when a customer took too long to validate their payment authentication. The user can check the payment details page on how to retry this payment.',
    defaultMessage: 'Expired',
  },
  incomplete: {
    id: 'conversations_ui.payment.status.incomplete',
    description: 'The short status indicator for a payment that is incomplete.',
    defaultMessage: 'Incomplete',
  },
  uncaptured: {
    id: 'conversations_ui.payment.status.uncaptured',
    description:
      'The short status indicator for a payment that has been authed but not yet captured.',
    defaultMessage: 'Uncaptured',
  },
  partialCapture: {
    id: 'conversations_ui.payment.status.partialCapture',
    description:
      'The short status indicator for a multi-capture payment that has been partially captured.',
    defaultMessage: 'Partial capture',
  },
  disputed: {
    id: 'conversations_ui.payment.status.disputed',
    description:
      'The short status indicator for a payment that has been disputed.',
    defaultMessage: 'Disputed',
  },
  earlyFraudWarning: {
    id: 'conversations_ui.payment.status.earlyFraudWarning',
    description:
      'The short status indicator for a payment received an early fraud warning.',
    defaultMessage: 'Early fraud warning',
  },
  pendingRefund: {
    id: 'conversations_ui.payment.status.pendingRefund',
    description:
      'The short status indicator for a payment whose refund is pending.',
    defaultMessage: 'Refund pending',
  },
  refundNeedsResponse: {
    id: 'conversations_ui.payment.status.refundNeedsResponse',
    description:
      'The short status indicator for when a refunded payment needs a response from the customer.',
    defaultMessage: 'Needs response',
  },
  refundDebitRequiresAction: {
    id: 'conversations_ui.payment.status.refundDebitRequiresAction',
    description:
      'The short status indicator for when a debit refund requires action.',
    defaultMessage: 'Requires action',
  },
  partiallyPaid: {
    id: 'conversations_ui.payment.status.status_partiallyPaid',
    description:
      'The short status indicator for a payment that has been partially funded.',
    defaultMessage: 'Partially paid',
  },
  multipleDisputes: {
    id: 'conversations_ui.payment.status.multipleDisputes',
    description:
      'Status text to indicate there are multiple disputes on this payment',
    defaultMessage: 'Multiple disputes',
  },
  prevented: {
    id: 'conversations_ui.payment.status.prevented',
    description: 'Status text to indicate a dispute was prevented.',
    defaultMessage: 'Prevented',
  },
  lost: {
    id: 'conversations_ui.payment.status.lost',
    description: 'The short status indicator when a payment dispute is lost.',
    defaultMessage: 'Dispute lost',
  },
  won: {
    id: 'conversations_ui.payment.status.won',
    description: 'Status text to indicate the dispute is won.',
    defaultMessage: 'Dispute won',
  },
  needsResponse: {
    id: 'conversations_ui.payment.status.needsResponse',
    description: 'The short status indicator when a payment needs a response.',
    defaultMessage: 'Needs response',
  },
});

type DisplayBadgeStatus =
  | 'disputePastDue'
  | 'needsResponseAutoRepAutoSubmit'
  | 'needsResponse'
  | 'unchallengeableInquiryPastDue'
  | 'inquiryPastDue'
  | 'preArbReceived'
  | 'preArbEscalated'
  | 'reviewPending'
  | 'daysTillResponse'
  | 'evidenceSubmittedAutoRepAutoSubmit'
  | 'evidenceSubmitted'
  | 'partiallyWon'
  | 'wonAutoRep'
  | 'won'
  | 'lostAutoRep'
  | 'lost'
  | 'inquiryClosed'
  | 'inquiryInReview'
  | 'refunded'
  | 'protected'
  | 'disputed'
  | 'prevented';

type DisplayStatus = DisplayBadgeStatus | Status;

type BadgeColors = 'blue' | 'gray' | 'green' | 'red' | 'yellow';

type Charge = {
  status?: string;
  refunded?: boolean;
  captured?: boolean;
  dispute?: any;
  outcome?: any;
  amount_refunded?: number;
  early_fraud_warning?: any;
  amount_captured?: number | null;
  failure_code?: string;
  refunds?: {
    data?: Array<{
      status?: string;
      type?: string;
      destination_details?: {
        card?: {
          type?: string;
        };
      };
    }>;
  };
  multiple_disputes?: boolean;
};

type PaymentIntent = {
  status?: string;
  amount_received?: number | null;
  cancellation_reason?: string;
  last_payment_error?: any;
};

type Refund = {
  status?: string;
  type?: string;
};

type StatusBadgeProps = {
  charge?: Charge;
  paymentIntent?: PaymentIntent;
  refund?: Refund;
};

const BADGE_COLOR_TYPE_MAP: Readonly<Record<BadgeColors, BadgeProps['type']>> =
  {
    blue: 'info',
    gray: 'neutral',
    green: 'positive',
    red: 'negative',
    yellow: 'warning',
  };

const BADGE_ICON_MAP: Record<string, IconAsset> = {
  arrowRight,
  block,
  cancel,
  check,
  clock,
  dispute,
  disputeProtection,
  earlyFraudWarning,
  fee,
  info,
  refund,
  review,
  trial,
  rule,
  shieldCheck,
};

function isPendingRefund(charge: Charge): boolean {
  const {amount_refunded: amountRefunded, refunded, refunds} = charge;
  return (
    (refunded || !!amountRefunded) &&
    typeof refunds === 'object' &&
    refunds?.data?.[0]?.status === 'pending'
  );
}

function isRefundDebit(charge: Charge): boolean {
  return charge.refunds?.data?.[0]?.type === 'refund_debit';
}

function hasLatestRefundAsReversal(charge: Charge): boolean {
  return (
    charge.refunds?.data?.[0]?.destination_details?.card?.type === 'reversal'
  );
}

function hasRefundRequiringAction(charge: Charge): boolean {
  const {amount_refunded: amountRefunded, refunded, refunds} = charge;
  if (
    (refunded || !!amountRefunded) &&
    typeof refunds === 'object' &&
    refunds?.data
  ) {
    return refunds.data.some((r) => r.status === 'requires_action');
  }
  return false;
}

function hasMultipleDisputes(charge: Charge): boolean {
  return !!charge.multiple_disputes;
}

function getDisplayStatus(
  charge?: Charge,
  paymentIntent?: PaymentIntent,
  refundObj?: Refund,
): DisplayStatus {
  if (refundObj) {
    const refundStatus = refundObj.status;
    if (refundStatus === 'pending') {
      return DISPLAY_STATUSES.pendingRefund;
    } else if (refundStatus === 'requires_action') {
      if (refundObj.type === 'refund_debit') {
        return DISPLAY_STATUSES.refundDebitRequiresAction;
      } else {
        return DISPLAY_STATUSES.refundNeedsResponse;
      }
    } else if (refundStatus === 'succeeded') {
      return DISPLAY_STATUSES.refunded;
    } else if (refundStatus === 'failed') {
      return DISPLAY_STATUSES.failed;
    } else if (refundStatus === 'canceled') {
      return DISPLAY_STATUSES.canceled;
    }
  }

  if (paymentIntent && !charge) {
    const piStatus = paymentIntent.status;
    const amountReceived = paymentIntent.amount_received;

    if (piStatus === 'requires_capture') {
      if (amountReceived != null && amountReceived > 0) {
        return DISPLAY_STATUSES.partialCapture;
      }
      return DISPLAY_STATUSES.uncaptured;
    } else if (piStatus === 'processing') {
      return DISPLAY_STATUSES.pending;
    } else if (piStatus === 'succeeded') {
      return DISPLAY_STATUSES.succeeded;
    } else if (
      piStatus === 'requires_payment_method' ||
      piStatus === 'requires_action' ||
      piStatus === 'requires_confirmation'
    ) {
      return DISPLAY_STATUSES.incomplete;
    } else if (piStatus === 'canceled') {
      if (
        paymentIntent.cancellation_reason === 'failed_invoice' &&
        paymentIntent.last_payment_error
      ) {
        return DISPLAY_STATUSES.failed;
      }
      return DISPLAY_STATUSES.canceled;
    }
  }

  if (charge) {
    const {
      refunded,
      captured,
      dispute: chargeDispute,
      outcome,
      status,
      amount_refunded: amountRefunded,
      early_fraud_warning: earlyFraudWarn,
      amount_captured: amountCaptured,
      failure_code: failureCode,
    } = charge;

    if (isPendingRefund(charge)) {
      return DISPLAY_STATUSES.pendingRefund;
    } else if (hasRefundRequiringAction(charge)) {
      if (isRefundDebit(charge)) {
        return DISPLAY_STATUSES.refundDebitRequiresAction;
      } else {
        return DISPLAY_STATUSES.refundNeedsResponse;
      }
    } else if (status === 'succeeded' && refunded && captured) {
      if (hasLatestRefundAsReversal(charge)) {
        return DISPLAY_STATUSES.reversed;
      }
      return DISPLAY_STATUSES.refunded;
    } else if (refunded && !captured) {
      return DISPLAY_STATUSES.canceled;
    } else if (outcome && outcome.type === 'blocked') {
      return DISPLAY_STATUSES.blocked;
    } else if (hasMultipleDisputes(charge)) {
      return DISPLAY_STATUSES.multipleDisputes;
    } else if (chargeDispute) {
      return DISPLAY_STATUSES.disputed;
    } else if (status === 'failed') {
      if (failureCode === 'payment_intent_payment_attempt_expired') {
        return DISPLAY_STATUSES.expired;
      }
      return DISPLAY_STATUSES.failed;
    } else if (status === 'pending') {
      return DISPLAY_STATUSES.pending;
    } else if (!captured) {
      if (amountCaptured && amountCaptured > 0) {
        return DISPLAY_STATUSES.partialCapture;
      }
      return DISPLAY_STATUSES.uncaptured;
    } else if (amountRefunded) {
      if (hasLatestRefundAsReversal(charge)) {
        return DISPLAY_STATUSES.partiallyReversed;
      }
      return DISPLAY_STATUSES.partialRefund;
    } else if (earlyFraudWarn) {
      return DISPLAY_STATUSES.earlyFraudWarning;
    }
  }

  return DISPLAY_STATUSES.succeeded;
}

function getBadgeColor(status: DisplayStatus): BadgeColors {
  switch (status) {
    case 'needsResponse':
    case 'disputePastDue':
    case 'inquiryPastDue':
    case 'unchallengeableInquiryPastDue':
    case 'partiallyPaid':
    case 'multipleDisputes':
    case 'preArbReceived':
      return 'yellow';
    case 'succeeded':
    case 'won':
    case 'partiallyWon':
    case 'wonAutoRep':
      return 'green';
    case 'refunded':
    case 'reversed':
    case 'partialRefund':
    case 'partiallyReversed':
    case 'incomplete':
    case 'canceled':
    case 'pending':
    case 'pendingRefund':
    case 'uncaptured':
    case 'disputed':
    case 'earlyFraudWarning':
    case 'refundNeedsResponse':
    case 'partialCapture':
    case 'inquiryClosed':
    case 'expired':
    case 'prevented':
    case 'needsResponseAutoRepAutoSubmit':
      return 'gray';
    case 'blocked':
    case 'failed':
    case 'lost':
    case 'lostAutoRep':
      return 'red';
    case 'daysTillResponse':
    case 'reviewPending':
    case 'evidenceSubmitted':
    case 'inquiryInReview':
    case 'preArbEscalated':
    case 'evidenceSubmittedAutoRepAutoSubmit':
      return 'blue';
    default:
      return 'gray';
  }
}

function getStatusIcon(
  status: DisplayStatus,
  charge?: Charge,
  paymentIntent?: PaymentIntent,
  refundObj?: Refund,
): IconAsset {
  switch (status) {
    case 'refunded':
    case 'reversed':
      return BADGE_ICON_MAP.refund;
    case 'blocked':
      return BADGE_ICON_MAP.block;
    case 'canceled':
      return charge?.refunded || refundObj
        ? BADGE_ICON_MAP.refund
        : BADGE_ICON_MAP.cancel;
    case 'failed':
    case 'lostAutoRep':
    case 'lost':
      return BADGE_ICON_MAP.cancel;
    case 'expired':
      return BADGE_ICON_MAP.trial;
    case 'incomplete':
    case 'pending':
    case 'pendingRefund':
    case 'uncaptured':
    case 'refundNeedsResponse':
    case 'refundDebitRequiresAction':
    case 'daysTillResponse':
    case 'reviewPending':
      return BADGE_ICON_MAP.clock;
    case 'partialRefund':
    case 'partiallyReversed':
    case 'needsResponse':
    case 'needsResponseAutoRepAutoSubmit':
    case 'disputePastDue':
    case 'inquiryPastDue':
    case 'unchallengeableInquiryPastDue':
    case 'multipleDisputes':
      return BADGE_ICON_MAP.info;
    case 'partiallyPaid':
    case 'partialCapture':
      return BADGE_ICON_MAP.fee;
    case 'evidenceSubmittedAutoRepAutoSubmit':
    case 'evidenceSubmitted':
      return BADGE_ICON_MAP.arrowRight;
    case 'inquiryInReview':
      return BADGE_ICON_MAP.review;
    case 'preArbEscalated':
    case 'preArbReceived':
      return BADGE_ICON_MAP.rule;
    case 'prevented':
      return BADGE_ICON_MAP.shieldCheck;
    case 'disputed': {
      const chargeDispute = charge?.dispute;
      const disputeCovered =
        chargeDispute &&
        typeof chargeDispute !== 'string' &&
        ((chargeDispute.dispute_protection_result &&
          chargeDispute.dispute_protection_result.is_covered) ||
          chargeDispute.covered_by_dispute_protection);
      return disputeCovered
        ? BADGE_ICON_MAP.disputeProtection
        : BADGE_ICON_MAP.dispute;
    }
    case 'earlyFraudWarning':
      return BADGE_ICON_MAP.earlyFraudWarning;
    case 'succeeded':
    case 'inquiryClosed':
    case 'partiallyWon':
    case 'wonAutoRep':
    case 'won': {
      const chargeProtected =
        charge?.outcome?.dispute_protection_result?.is_covered;
      return chargeProtected
        ? BADGE_ICON_MAP.disputeProtection
        : BADGE_ICON_MAP.check;
    }
    default:
      return BADGE_ICON_MAP.check;
  }
}

export const StatusBadge = ({
  charge,
  paymentIntent,
  refund: refundObj,
}: StatusBadgeProps) => {
  const intl = useIntl();
  const status = getDisplayStatus(charge, paymentIntent, refundObj);
  const color = getBadgeColor(status);
  const icon = getStatusIcon(status, charge, paymentIntent, refundObj);
  const type = BADGE_COLOR_TYPE_MAP[color];

  const statusMessage = statusMessages[status as keyof typeof statusMessages];
  const statusText = statusMessage ? intl.formatMessage(statusMessage) : status;

  if (!icon || !type || !statusText) {
    return null;
  }

  return (
    <view.div
      css={{
        display: 'flex',
        width: 'fill',
      }}
    >
      <Badge
        type={type}
        css={{
          maxWidth: 'fill',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <view.span
          css={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 'space.0',
          }}
        >
          {statusText}
        </view.span>
        <view.span
          css={{
            marginLeft: 'xxsmall',
            display: 'flex',
            flexShrink: 0,
          }}
        >
          <Icon icon={icon} size="xsmall" />
        </view.span>
      </Badge>
    </view.div>
  );
};
